import './Communications.css'
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import BlogContext, { BlogContextInterface, MediaItem, Publication } from '../../context/CommunicationsContext'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const sortKey = (date: string) => (date.length === 7 ? date + '-99' : date);

function sortNewestFirst<T extends { date: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => sortKey(b.date).localeCompare(sortKey(a.date)));
}

function groupByYear<T extends { date: string }>(items: T[]): { year: string, items: T[] }[] {
  const groups: { year: string, items: T[] }[] = [];
  sortNewestFirst(items).forEach((item) => {
    const year = item.date.slice(0, 4);
    const last = groups[groups.length - 1];
    if (last && last.year === year) {
      last.items.push(item);
    } else {
      groups.push({ year, items: [item] });
    }
  });
  return groups;
}

function formatDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  if (!month) return String(year);
  if (!day) return `${MONTHS[month - 1]} ${year}`;
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

const withStop = (text: string) => (/[.?!]$/.test(text) ? text : `${text}.`);

function citationDetails(pub: Publication): string {
  if (pub.volume) {
    return ` ${pub.volume}${pub.issue ? `(${pub.issue})` : ''}${pub.pages ? `: ${pub.pages}` : ''}.`;
  }
  return pub.note ? `. ${withStop(pub.note)}` : '.';
}

const fillIn = (text: string, values: Record<string, number>) =>
  text.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ''));

const Emphasis = ({ text }: { text: string }) => (
  <>
    {text.split(/\*([^*]+)\*/).map((part, index) => (index % 2 ? <em key={index}>{part}</em> : part))}
  </>
);

const Authors = ({ pub }: { pub: Publication }) => (
  <>
    {pub.authors.map((author, index) => (
      <span key={author + index}>
        {pub.boldAuthors.includes(author) ? <strong>{author}</strong> : author}
        {index < pub.authors.length - 1 ? ', ' : ''}
      </span>
    ))}
  </>
);

export function Blog() : JSX.Element {

  const { top, publications, media } = useContext(BlogContext) as BlogContextInterface;

  const publicationGroups = groupByYear(publications.items);
  const mediaGroups = groupByYear<MediaItem>(media.items);

  let publicationsAbove = 0;

  const introValues = {
    papers: publications.items.length,
    firstAuthor: publications.items.filter((pub) => pub.authors[0]?.startsWith('Emogor')).length,
  };

  return(
    <div className='Blog'>
      {/* @ts-ignore */}
      <Helmet>
          <title>Communications - Pangolino.org</title>
          <meta name="description" content='Our published research on pangolins and wild meat hunting, and media coverage of our work.'></meta>
          <link rel="canonical" href="/communications"></link>
      </Helmet>

      <div className='Blog-Body'>
        <div className='Publications-Banner'>
          <img
            src='/images/donations/publications-banner.jpg'
            alt="Researchers fitting a tracking tag to a pangolin"
          />
          <p className='Publications-Banner-Credit'>Photo by Edet Etim</p>
        </div>

        <div className='Publications'>
          <header className='Publications-Header'>
            <h1>{top.title}</h1>
            <p className='Publications-Header-Lead'><Emphasis text={fillIn(top.description, introValues)} /></p>
            {top.details && <p className='Publications-Header-Lead'><Emphasis text={fillIn(top.details, introValues)} /></p>}
            {top.note && <p className='Publications-Header-Note'>{top.note}</p>}
          </header>

          <section className='Publications-Section' id='publications'>
            <h2>{publications.title}</h2>
            {publicationGroups.map((group) => {
              const start = publications.items.length - publicationsAbove;
              publicationsAbove += group.items.length;
              return (
                <div className='Publications-Year' key={group.year}>
                  <h3>{group.year}</h3>
                  <ol reversed start={start}>
                    {group.items.map((pub) => (
                      <li key={pub.doi}>
                        <Authors pub={pub} /> ({pub.date.slice(0, 4)}) {withStop(pub.title)} <em>{pub.journal}</em>{citationDetails(pub)} Doi:{' '}
                        <a href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener noreferrer">{pub.doi}</a>
                      </li>
                    ))}
                  </ol>
                </div>
              );
            })}
          </section>

          <section className='Publications-Section' id='news'>
            <h2>{media.title}</h2>
            {mediaGroups.map((group) => (
              <div className='Publications-Year' key={group.year}>
                <h3>{group.year}</h3>
                <ul>
                  {sortNewestFirst(group.items).map((item) => (
                    <li key={item.link}>
                      <a href={item.link} target="_blank" rel="noopener noreferrer">{item.title}</a>{/[.?!]$/.test(item.title) ? '' : '.'} <em>{item.publisher}</em>, {formatDate(item.date)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  )
}
